import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { SxProps, Typography, TypographyProps } from '@mui/material';
import { Asset, Maybe, Page, NavigationLink as TNavigationLink, AnalyticsContext, BlogDetailsPage, FacilityDetailsPage, ServiceDetailsPage } from '@/types/generated';
import ImageWrapper from '@shared-components/components/ImageWrapper';
import { resolveNavLinkText } from '@shared-components/components/NavigationLink/NavigationLink';
import { imageStyles } from '@shared-components/components/NavigationLink/NavigationLink.styles';
import TAGLink from '@shared-components/components/TAGLink';
import TAGSvgIcon from '@shared-components/components/TAGSvgIcon';
import { useAppContext, useFacilityContext, useOfficeLocationContext } from '@shared-components/context';
import { resolveEntryLink } from '@utils/resolveButtonEntryLink';
export interface IGroupNavMobileLinkProps extends TNavigationLink {
  isInternal: boolean;
  linkText?: Maybe<string>;
  pageData?: Maybe<Page | BlogDetailsPage | FacilityDetailsPage | ServiceDetailsPage>;
  externalUrl?: Maybe<string>;
  openInNewTab?: Maybe<boolean>;
  linkIcon?: Maybe<Asset>;
  dataTestId?: string;
  analyticsContext?: Maybe<AnalyticsContext>;
  linkSx?: SxProps;
  textVariant?: TypographyProps['variant'];
  textSx?: SxProps;
  showIconText?: boolean;
  iconSize?: number;
  trailingIcon?: boolean;
  chevronIcon?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  ariaControls?: string;
  ariaHasPopup?: boolean;
}

/**
 * Bare-bones link used only by the GroupNavList mobile drill-down.
 * Unlike NavigationLink, it applies no default CSS - callers supply all styling via linkSx/textSx.
 */
export default function GroupNavMobileLink({
  linkText,
  pageData,
  linkIcon,
  isInternal,
  externalUrl,
  openInNewTab,
  analyticsContext,
  dataTestId = 'nav_link',
  linkSx,
  textVariant,
  textSx,
  showIconText = false,
  iconSize,
  trailingIcon = false,
  chevronIcon = false,
  onClick,
  className,
  ariaControls,
  ariaHasPopup
}: Readonly<IGroupNavMobileLinkProps>) {
  const {
    config
  } = useAppContext();
  const {
    officeInfo
  } = useFacilityContext();
  const {
    officeInfo: officeLocationInfo
  } = useOfficeLocationContext();
  const href = isInternal ? resolveEntryLink({
    path: pageData?.path ?? '',
    typeName: pageData?.__typename as string,
    config,
    officeInfo,
    officeLocationInfo
  }) : externalUrl ?? '#';
  const text = resolveNavLinkText({
    linkText,
    isInternal,
    pageData,
    externalUrl
  });
  const withIcon = !!linkIcon?.url || false;
  const title = (isInternal ? pageData?.title : externalUrl) ?? undefined;
  const showDisplayText = withIcon && showIconText || !withIcon;
  return <TAGLink title={title} href={href || '#'} dataTestId={dataTestId} analyticsContext={analyticsContext} openInNewTab={openInNewTab ?? false} linkSx={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    ...linkSx
  }} onClick={onClick} className={className} ariaControls={ariaControls} ariaHasPopup={ariaHasPopup} data-sentry-element="TAGLink" data-sentry-component="GroupNavMobileLink" data-sentry-source-file="GroupNavMobileLink.tsx">
			{withIcon && <ImageWrapper src={linkIcon?.url as string} alt={linkIcon?.description ?? ''} style={imageStyles(iconSize) as React.CSSProperties} />}
			{showDisplayText && <>
					<Typography component="span" variant={textVariant} sx={{
        color: 'text.interactive',
        ...textSx
      }}>
						{text}
					</Typography>
					{trailingIcon && <ArrowForwardIcon fontSize="small" sx={{
        color: 'tertiary.main'
      }} data-testid={`${dataTestId}_arrow`} />}
					{chevronIcon && <Typography component="span" color="tertiary.main" sx={{
        display: 'inline',
        lineHeight: 'normal'
      }}>
							<TAGSvgIcon fontSize="small" icon="ChevronRightFunc" size={24} />
						</Typography>}
				</>}
		</TAGLink>;
}